<template>
    <div class="vehicle-check-availability" :class="'vehicle-check-availability--' + mode">
        <template v-if="renterCanNotBookBaloiseVehicle">
            <Notice type="warning" class="vehicle-check-availability__error">
                {{ $t('pages.booking_request.baloise_vehicle_renting_disallowed_notice') }}
            </Notice>
            <button class="button button--primary button--full-width" @click="applyFilter">
                {{ $t('modals.insurance_limitation.show_available_vehicles') }}
            </button>
        </template>
        <template v-else-if="selectedDates.from && selectedDates.to">
            <div v-if="$v.selectedDates.$invalid" class="vehicle-check-availability__error">
                <Notice v-if="!$v.selectedDates.from.validRange || !$v.selectedDates.to.validRange" type="info">
                    {{ $t('booking_request_form.error_invalid_range') }}
                </Notice>
                <Notice v-else-if="!$v.selectedDates.from.minLength || !$v.selectedDates.to.minLength" type="warning">
                    {{ $t('booking_request_form.error_too_short', { minLength: minBookingLengthForDates }) }}
                </Notice>
                <Notice v-else-if="!$v.selectedDates.from.maxLength || !$v.selectedDates.to.maxLength" type="warning">
                    {{ $t('booking_request_form.error_too_long', { maxLength: 90 }) }}
                </Notice>
            </div>
            <Notice v-else-if="notAllowedToRentError" type="warning" class="vehicle-check-availability__error">
                {{ notAllowedToRentError }}
            </Notice>

            <div class="vehicle-check-availability__content vehicle-check-availability__content-selected">
                <div class="vehicle-check-availability__items">
                    <VehiclePricing
                        class="vehicle-check-availability__value vehicle-check-availability__price"
                        :vehicle="vehicle"
                        :pricing="vehicle.pricing"
                        :is-loading="isLoadingPrice"
                        :load-pricing-error="loadPricingError"
                        :from-date="selectedDates.from"
                        :to-date="selectedDates.to"
                        mode="total-only"
                    />
                    <DateSelectInput
                        class="dates-link vehicle-check-availability__value"
                        :init-from="selectedDates.from"
                        :init-to="selectedDates.to"
                        input-type="link"
                        :high-season-min-booking-length="vehicle.minBookingLengthHigh"
                        :low-season-min-booking-length="vehicle.minBookingLengthLow"
                        :disabled="!featureEnabled('renterAccess')"
                        :disabled-dates="vehicle.unavailableDates"
                        :add-to-query-immediately="true"
                    >
                        {{ selectedDatesFormatted }}
                    </DateSelectInput>
                </div>

                <button
                    class="button button--primary vehicle-check-availability__button"
                    :disabled="$v.selectedDates.$invalid || !vehicleIsBookable"
                    @click="$emit('click')"
                >
                    <template v-if="vehicle.bookingMethod === 'instant'">
                        {{ ctaTexts.instant }}
                    </template>
                    <template v-else>{{ ctaTexts.binding }}</template>
                </button>
            </div>
        </template>
        <div v-else class="vehicle-check-availability__content-selected">
            <div
                v-if="isNewBookingProcessOptimisationEnabled"
                class="vehicle-check-availability__check-price-container"
            >
                <span>{{ $t('common.from') }}</span>
                <i18n path="pages.vehicle.sticky_header.nightly_price_alternative" tag="span">
                    <template #price>
                        <strong>{{ nightlyPrice }}</strong>
                    </template>
                </i18n>
            </div>
            <span v-else class="vehicle-check-availability__nightly-price">{{ nightlyPriceTxt }}</span>

            <DateSelectInput
                :init-from="selectedDates.from"
                :init-to="selectedDates.to"
                input-type="button"
                :high-season-min-booking-length="vehicle.minBookingLengthHigh"
                :low-season-min-booking-length="vehicle.minBookingLengthLow"
                :disabled="!featureEnabled('renterAccess')"
                :disabled-dates="vehicle.unavailableDates"
                :placeholder="ctaTexts.checkAvailability"
                :add-to-query-immediately="true"
            />
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import parseISO from 'date-fns/parseISO';
import getYear from 'date-fns/getYear';
import { getFilterValue, buildQuery } from '~/helpers/queryParser';
import { AVAILABLE_BETWEEN, RENTABLE_OUTSIDE_OF_SWITZERLAND } from '~/config/filterKeys';
import modalMixin from '~/mixins/modalMixin';
import DateSelectInput from '~/components/form-elements/DateSelectInput.vue';
import VehiclePricing from '~/components/vehicles/VehiclePricing.vue';
import Notice from '~/components/Notice.vue';

export default {
    components: { DateSelectInput, VehiclePricing, Notice },
    mixins: [validationMixin, modalMixin],
    validations: {
        selectedDates: {
            from: {
                required,
                validRange() {
                    return this.$validators.isValidRange(
                        this.vehicle.unavailableDates,
                        this.selectedDates.from,
                        this.selectedDates.to
                    );
                },
                minLength() {
                    return this.$validators.isRangeLongEnough(
                        this.minBookingLengthForDates,
                        this.selectedDates.from,
                        this.selectedDates.to
                    );
                },
                maxLength() {
                    return this.$validators.isRangeShorterThanMax(this.selectedDates.from, this.selectedDates.to);
                },
            },
            to: {
                required,
                validRange() {
                    return this.$validators.isValidRange(
                        this.vehicle.unavailableDates,
                        this.selectedDates.from,
                        this.selectedDates.to
                    );
                },
                minLength() {
                    return this.$validators.isRangeLongEnough(
                        this.minBookingLengthForDates,
                        this.selectedDates.from,
                        this.selectedDates.to
                    );
                },
                maxLength() {
                    return this.$validators.isRangeShorterThanMax(this.selectedDates.from, this.selectedDates.to);
                },
            },
        },
    },
    props: {
        vehicle: {
            type: Object,
            default: () => {},
        },
        mode: {
            type: String,
            default: 'default', // 'default' or 'slim'
        },
        isLoadingPrice: {
            type: Boolean,
            default: false,
        },
        loadPricingError: {
            type: Boolean,
            default: false,
        },
        notAllowedToRentError: {
            type: String,
            default: null,
        },
        renterCanNotBookBaloiseVehicle: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...mapGetters(['isLargeScreen', 'isNewBookingProcessOptimisationEnabled']),
        ctaTexts() {
            return {
                instant: this.isLargeScreen
                    ? this.$t('booking_request_form.book_instantly')
                    : this.$t('booking_request_form.book_instantly_mobile'),
                binding: this.isLargeScreen
                    ? this.$t('booking_request_form.send_request_button')
                    : this.$t('booking_request_form.send_request_button_mobile'),
                checkAvailability: this.isLargeScreen
                    ? this.$t('booking_request_form.select_dates_button')
                    : this.$t('booking_request_form.select_dates_button_mobile'),
            };
        },
        selectedDates() {
            return getFilterValue(this.$route.query, AVAILABLE_BETWEEN);
        },
        selectedDatesFormatted() {
            const { dateFormat } = this.$options.filters;
            const start = dateFormat(this.selectedDates.from, 'd MMM');
            let end = dateFormat(this.selectedDates.to, 'd MMM');
            const startYear = getYear(parseISO(this.selectedDates.from));
            const endYear = getYear(parseISO(this.selectedDates.to));

            if (startYear !== endYear) {
                end += ` ${endYear}`;
            }

            return `${start} - ${end}`;
        },
        vehicleIsBookable() {
            return this.featureEnabled('renterAccess');
        },
        minBookingLengthForDates() {
            const { from, to } = this.selectedDates;

            if (this.$utils.isOnlyHighSeason(from, to)) {
                return this.vehicle.minBookingLengthHigh;
            }

            if (this.$utils.isOnlyLowSeason(from, to)) {
                return this.vehicle.minBookingLengthLow;
            }

            // Mixed season, take the "loosest" rule
            return Math.min(this.vehicle.minBookingLengthHigh, this.vehicle.minBookingLengthLow);
        },
        nightlyPrice() {
            const { pricing } = this.vehicle;

            const nightlyPrice = pricing.lowNightlyPrice;

            const amount = this.$root.$options.filters.price(nightlyPrice) || 0;
            const formatted = this.$root.$options.filters.formatAsPrice(amount, pricing.currency, 0);

            return formatted;
        },
        nightlyPriceTxt() {
            return this.$t('pages.vehicle.sticky_header.nightly_price', { price: this.nightlyPrice });
        },
    },
    methods: {
        applyFilter() {
            const query = buildQuery(this.$route.query, { [RENTABLE_OUTSIDE_OF_SWITZERLAND]: true });

            this.$router.push(this.localePath({ path: this.$utils.getVehicleSearchPagePath(this.vehicle), query }));
        },
    },
};
</script>

<style lang="scss">
@import '@/sass/_variables.scss';

.vehicle-check-availability {
    &__content-selected {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    &__value {
        text-align: left;
    }

    &__items {
        margin-right: 16px;
    }

    &__price {
        font-weight: bold;
        .loading-indicator {
            text-align: left;
            vertical-align: middle;
            display: inline-block;
        }
    }

    &__price,
    &__value .date-select-input__link {
        white-space: nowrap;
        line-height: 24px;
        height: 24px;
    }

    &__button {
        min-width: 180px;
        padding: 14px 10px;
    }

    &__error {
        text-align: left;
        margin-bottom: 10px;
    }

    .date-select-input > .button {
        width: 100%;
    }

    &__check-price-container {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        margin-right: 16px;

        span {
            @include font-size(14px);
        }

        strong {
            font-weight: bold;
            @include font-size(20px);
        }
    }

    &__nightly-price {
        font-weight: bold;
        margin-right: 16px;
    }
}
</style>
